"use client";

import { ArrowsClockwise } from "@repo/ui/components/icons";
import { Button } from "@repo/ui/components/ui/button";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { SITE_VERSION } from "../../../lib/version";
import { BrandLogo } from "../BrandLogo";
import { PWA_PHASE3 } from "./flags";
import { applyUpdate, checkForUpdate, useUpdateState } from "./updates";

const HOUR = 3_600_000;
const MINUTE = 60_000;

// Mounted once in the root layout (F173): checks /version.json on load, when
// the tab or installed app comes back to the foreground, every hour, and after
// navigations (at most once a minute); shows the required-update screen.
// The non-required card lives on /app (UpdateCard).
export function UpdateWatcher() {
  const pathname = usePathname();
  const first = useRef(true);

  useEffect(() => {
    if (!PWA_PHASE3) return;
    void checkForUpdate();
    const onVisible = () => document.visibilityState === "visible" && void checkForUpdate();
    document.addEventListener("visibilitychange", onVisible);
    const hourly = setInterval(() => void checkForUpdate(), HOUR);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(hourly);
    };
  }, []);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (PWA_PHASE3) void checkForUpdate(MINUTE);
  }, [pathname]);

  return <RequiredUpdateScreen pathname={pathname} />;
}

// A full screen in the style of the loading screen: nothing behind it can be
// used until «بروزرسانی». The bank's return page (/payment/<id>) shows its
// result first; the screen comes on the next navigation.
function RequiredUpdateScreen({ pathname }: { pathname: string }) {
  const update = useUpdateState();
  const [busy, setBusy] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const show = update.kind === "required" && !pathname.startsWith("/payment/");

  useEffect(() => {
    if (!show) return;
    button.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="required-update-title"
      className="bg-canvas fixed inset-0 z-[100] flex items-center justify-center px-4 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+6rem)]"
    >
      <div className="flex w-full max-w-sm flex-col items-center text-center">
        <span className="bg-background mb-6 flex h-16 w-16 items-center justify-center rounded-3xl shadow-sm">
          <BrandLogo className="h-10" />
        </span>
        <h1 id="required-update-title" className="text-xl font-extrabold tracking-tight">
          بروزرسانی به نسخه جدید
        </h1>
        <p className="text-muted-foreground mt-2 text-sm leading-6">
          برای ادامه، شمشاپ را به نسخه <span dir="ltr">v{update.version}</span> بروزرسانی کنید.
        </p>
        <Button
          ref={button}
          size="lg"
          className="mt-8 w-full"
          loading={busy}
          onClick={() => {
            setBusy(true);
            void applyUpdate();
          }}
        >
          <ArrowsClockwise /> بروزرسانی
        </Button>
        <p className="text-muted-foreground mt-4 text-xs">
          نسخه فعلی: <span dir="ltr">v{SITE_VERSION}</span>
        </p>
      </div>
    </div>
  );
}
